import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { userConfigApi } from '../lib/api';
import { UserConfig as UserConfigRecord } from '../lib/types';
import { Plus } from 'lucide-react';
import { UserConfigDialog } from '../components/config/UserConfigDialog';
import { TestTradeDialog } from '../components/config/TestTradeDialog';
import { TradingAPIInformation } from '../components/config/TradingAPIInformation';
import { UserConfigTable } from '../components/config/UserConfigTable';

export const UserConfigPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('edit');
  const [selectedConfig, setSelectedConfig] = useState<UserConfigRecord | null>(null);
  const [testTradeConfig, setTestTradeConfig] = useState<{ userId: string; apiKey: string } | null>(null);
  
  const apiUrl = import.meta.env.VITE_API_URL || 'Not configured';
  const tradeEndpoint = apiUrl === 'Not configured' ? 'Not configured' : `${apiUrl.replace(/\/+$/, '')}/trade`;

  const { data: configs = [], isLoading, error } = useQuery({
    queryKey: ['userConfigs'],
    queryFn: async () => {
      const response = await userConfigApi.getAll();
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: userConfigApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfigs'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UserConfigRecord) => userConfigApi.update(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfigs'] });
      setIsDialogOpen(false);
      setSelectedConfig(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: UserConfigRecord) => userConfigApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfigs'] });
      setIsDialogOpen(false);
      setSelectedConfig(null);
      setDialogMode('edit');
    },
  });

  const handleDelete = async (config: UserConfigRecord) => {
    if (window.confirm(`Are you sure you want to delete configuration for "${config.name}"?`)) {
      deleteMutation.mutate(config.id);
    }
  };

  const handleEdit = (config: UserConfigRecord) => {
    setDialogMode('edit');
    setSelectedConfig(config);
    setIsDialogOpen(true);
  };

  const handleAddUser = () => {
    setDialogMode('create');
    setSelectedConfig({
      id: '',
      name: '',
      email: '',
      bybitTestnetApiKey: '',
      bybitTestnetApiSecret: '',
      bybitProductionApiKey: '',
      bybitProductionApiSecret: '',
      testnetApiKey: '',
      productionApiKey: '',
      proxyHost: '',
      proxyPort: undefined,
      proxyUsername: '',
      proxyPassword: '',
      longConfig: '',
      shortConfig: '',
      tradingEnabled: false,
      cognitoUserId: '',
      notificationEmail: '',
      notificationsEnabled: false,
      notificationStatus: 'disabled',
      snsSubscriptionArn: undefined,
      reportsEnabled: false,
      sesStatus: 'disabled',
      sesIdentityArn: undefined,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (data: UserConfigRecord) => {
    if (dialogMode === 'create') {
      await createMutation.mutateAsync(data);
    } else {
      await updateMutation.mutateAsync(data);
    }
  };

  const visibleConfigs = configs.filter((config) => !config.id?.startsWith('TRADE_'));

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">User Configuration</h1>
          <div className="flex gap-3">
            <Button onClick={handleAddUser}>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>

        <TradingAPIInformation apiUrl={apiUrl} tradeEndpoint={tradeEndpoint} />

        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-md">
            Error loading configurations: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : visibleConfigs.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No trading configurations found</p>
          </div>
        ) : (
          <UserConfigTable
            configs={visibleConfigs}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onTestTrade={(config) => setTestTradeConfig({ userId: config.id, apiKey: config.testnetApiKey })}
          />
        )}

        <UserConfigDialog
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedConfig(null);
            setDialogMode('edit');
          }}
          config={selectedConfig}
          onSubmit={handleSubmit}
          mode={dialogMode}
          isSubmitting={dialogMode === 'create' ? createMutation.isPending : updateMutation.isPending}
        />

        {testTradeConfig && (
          <TestTradeDialog
            isOpen={!!testTradeConfig}
            onClose={() => setTestTradeConfig(null)}
            userId={testTradeConfig.userId}
            apiKey={testTradeConfig.apiKey}
          />
        )}
      </div>
    </Layout>
  );
};
